import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import * as React from 'react';

import { ContextStore, QueryCombiner, RouteStore, SerializedData } from 'bernie-core';
import { ErrorBoundary } from 'bernie-view';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

import { CurrentHotelStore } from 'stores/current-hotel-store';
import { InfositeQueryAdapter } from 'stores/adapters/infosite-query-adapter';
import { SearchCriteriaModel, singleUseQueryParams } from 'stores/models';
import { SearchQueryAdapter } from 'stores/adapters/search-query-adapter';

import { UitkSpacing } from 'uitk-react-spacing';

import { multiItemCompleteSearchCriteria } from 'components/multi-item/utils';

import { parse } from 'query-string';
import { SearchWizardComposer, SearchWizardComposerProps } from './search-wizard-composer';

export interface SingleOfferSearchFormProps {
  context?: ContextStore;
  currentHotel?: CurrentHotelStore;
  routeStore?: RouteStore;
  forceHistoryPushOnGoBack?: () => void;
  history?: { push: (url: string) => void };
}
type LocalizedSingleOfferSearchFormProps = LocalizedComponentProps & SingleOfferSearchFormProps;

export const SingleOfferSearchForm: React.FunctionComponent<LocalizedSingleOfferSearchFormProps> = props => (
  <WrappedSingleOffer {...props} />
);

SingleOfferSearchForm.displayName = 'SingleOfferSearchForm';

@withRouter
@withLocalization
@inject('currentHotel', 'context', 'routeStore')
@observer
// This should not be exported, but the existing tests rely too much on the implementation details. Do not import this!
export class WrappedSingleOffer extends React.Component<LocalizedSingleOfferSearchFormProps> {
  constructor(props: LocalizedSingleOfferSearchFormProps) {
    super(props);

    this.handleUpdateResults = this.handleUpdateResults.bind(this);
    this.onUpdateResults = this.onUpdateResults.bind(this);
    this.navigateToHsr = this.navigateToHsr.bind(this);
    this.navigateTo = this.navigateTo.bind(this);
  }

  private navigateToHsr({ formData }) {
    const { history, currentHotel } = this.props;
    history.push(currentHotel.getUpdatedBackToHsrUrl(formData));
  }

  private navigateTo({ formData, submitUrl }): Promise<any> {
    const { context, history, currentHotel } = this.props;

    return currentHotel.fetchPropertyAvailabilityCheck(context, formData).then(({ data }) => {
      const { propertyInfo } = data;
      if (propertyInfo.offers.errorMessage !== null || !propertyInfo.offers.listings?.length) {
        history.push(currentHotel.getUpdatedBackToHsrUrl(formData));
      } else {
        history.push(submitUrl);
      }
    });
  }

  private onUpdateResults(searchCriteria: SearchCriteriaModel) {
    this.handleUpdateResults(searchCriteria.toJSON(multiItemCompleteSearchCriteria), (prevQuery, newQuery) => newQuery);
  }

  private handleUpdateResults(criteria: SerializedData, queryCombinerFunction?: QueryCombiner) {
    const { context, l10n, routeStore } = this.props;

    const params = { ...criteria, localDateFormat: context.localDateFormat };
    const keysToKeepFromOriginalQuery = key => !key.startsWith('pwaDialog') && !key.startsWith('rm') && !singleUseQueryParams.includes(key);

    const queryString = routeStore.mergeQueryString(
      routeStore.search,
      InfositeQueryAdapter.parseOut(params, l10n),
      keysToKeepFromOriginalQuery
    );

    if (typeof this.props.forceHistoryPushOnGoBack === 'function') {
      this.props.forceHistoryPushOnGoBack();
    }

    const queryObj = parse(routeStore.search);
    const hasOpenDialog = Boolean(queryObj.pwaDialog);

    if (hasOpenDialog) {
      routeStore.rewriteHistory({ search: queryString }, -1);
    } else {
      routeStore.updateSearch(queryString, undefined, true, queryCombinerFunction);
    }
  }

  public componentDidMount() {
    const { currentHotel } = this.props;
    const { searchCriteria } = currentHotel;

    const { startDate, endDate, adults, children } = SearchQueryAdapter.parseOut(searchCriteria);
    // 0 - children from search is undefined
    const childrenFormatted = typeof children === 'undefined' ? '' : children;

    // set offer data on mount
    currentHotel.setOfferData({ startDate, endDate, adults, children: childrenFormatted });
  }

  public render() {
    const { currentHotel } = this.props;
    const errorMessage = currentHotel?.offers?.errorMessage;
    const singleUnitOffer = currentHotel?.offers?.singleUnitOffer;
    const searchWizardComposerProps: SearchWizardComposerProps = {
      navigateTo: currentHotel.defaultedDates ? this.navigateToHsr : this.navigateTo,
      onUpdateResults: this.onUpdateResults,
      showFormBtn: Boolean(currentHotel?.defaultedDates) || errorMessage?.length > 0 || !singleUnitOffer?.ratePlans,
      dateless: currentHotel.defaultedDates,
    };

    return (
      <ErrorBoundary label="hotel.information.searchform.singleoffer">
        <UitkSpacing padding={{ inline: 'three' }}>
          <div>
            <SearchWizardComposer {...searchWizardComposerProps} />
          </div>
        </UitkSpacing>
      </ErrorBoundary>
    );
  }
}

export default SingleOfferSearchForm;
